import { lazy, useState } from 'react'
import googleCalendarIcon from '@assets/svg/google-calendar-icon.svg'
import { useUser } from '@features/auth/hooks'
import { useCurrentEvent } from '@features/event/hooks'
import { createGoogleCalendarUrl } from '@features/event/utils/create-google-calendar-url'
import { useIsHost } from '@features/event/utils/is-host'
import { useLanguages } from '@features/languages/hooks'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import ReactPlayer from 'react-player'
import { useNavigate } from 'react-router-dom'
import {
  eventApi,
  EventResponse,
  PublicUserProfile,
  useEventDeleteParticipationInEventMutation,
  useEventGetEventConfigQuery,
  useEventParticipateInEventMutation,
  useEventStartNowMutation,
} from '@app/services'
import {
  Avatar,
  Box,
  Button,
  Container,
  Countdown,
  Grid2 as Grid,
  Link,
  Stack,
  Typography,
} from '@common/components'
import { MY_CLASSROOMS_ROUTE } from '@common/constants/routes'
import { useAppDispatch, useMediaQuery } from '@common/hooks'
import { SparklesIcon } from '@common/icons'
import { handleGravatar } from '@common/utils/gravatar'
import { HowItWorksEvent } from '../how-it-works-event'
import CancelEvent from './cancel-event'
import { ModeTypography } from './loading-screen.styles'

const Illustration = lazy(() => import('./illustration'))

const LoadingScreen = (props: { type: string; event: EventResponse }) => {
  const { type, event } = props
  const { user } = useUser()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  //   const { event, config, statusStartEvent } = useSelector(
  //     (state) => state.currentEvent
  //   )
  //   const { statusParticipateInEvent, statusCancelParticipation } = useSelector(
  //     (state) => state.events
  //   )
  const [showExpectations, setShowExpectations] = useState(false)
  const { t } = useTranslation()
  const [participateInEvent, { isLoading: isLoadingParticipateInEvent }] =
    useEventParticipateInEventMutation()
  const [cancelParticipation, { isLoading: isLoadingCancelParticipant }] =
    useEventDeleteParticipationInEventMutation()
  const [startEvent, { isLoading: isLoadingStartEvent }] = useEventStartNowMutation()
  const { data: config } = useEventGetEventConfigQuery({ eventId: event?.event_id })
  const isHost = useIsHost(user?.id, event?.host?.id)

  const handleParticipate = async () => {
    const patchResult = dispatch(
      eventApi.util.updateQueryData(
        'eventGetEvents',
        {
          isDashboard: false,
        },
        (draft) => {
          draft.events
            ?.find((e) => e.event_id === event.event_id)
            ?.participants.push({ id: user?.id, username: user?.username, image: user?.image }) // Add user to participants list
        },
      ),
    )
    try {
      await participateInEvent({ eventId: event.event_id }).unwrap()
    } catch {
      patchResult.undo()
    }
  }
  const handleCancelParticipation = async () => {
    const patchResult = dispatch(
      eventApi.util.updateQueryData(
        'eventGetEvents',
        {
          isDashboard: false,
        },
        (draft) => {
          const targetEvent = draft.events?.find((e) => e.event_id === event.event_id)
          const filteredEvent = draft.events?.filter((e) => e.event_id !== event.event_id) || []

          if (!targetEvent) return

          draft.events = [
            ...filteredEvent,
            {
              ...targetEvent,
              participants: targetEvent.participants.filter(
                (participant) => participant.id !== user?.id,
              ),
            },
          ]
        },
      ),
    )
    try {
      await cancelParticipation({ eventId: event.event_id }).unwrap()
    } catch {
      patchResult.undo()
    }
  }

  const handleStartEvent = () => {
    startEvent({ eventId: event.event_id })
  }

  if (type === 'scheduled') {
    return (
      <Layout
        startTime={event.start_time_utc}
        headerTitle={
          <Button
            onClick={() => navigate(MY_CLASSROOMS_ROUTE)}
            variant='text'
            color='neutral'
            sx={{ ml: 'auto', width: '120px', display: 'block' }}
          >
            {t('loadingScreen.exit', 'Exit')}
          </Button>
        }
        lobbyTitle={'Participants'}
        eventId={event.short_event_id ? event.short_event_id : event.event_id}
        users={event.participants}
        illustrationChildren={
          <UpcomingEventDetails
            title={event.event_title}
            host={event.host}
            language={event.event_language}
            start_time={dayjs.utc(event?.start_time_utc, 'YYYY-MM-DDTHH:mm:ss').toDate()}
          />
        }
        minNumberUsers={config?.min_number_users}
        maxNumberUsers={config?.max_number_users}
      >
        {showExpectations ? (
          <>
            <HowItWorksEvent
              topicLink={event.lesson_link}
              onClose={() => setShowExpectations(false)}
            />
          </>
        ) : (
          <>
            <Stack spacing={2} justifyContent='center' alignItems='center' maxWidth='400px' mb={4}>
              <ReactPlayer
                volume={0.8}
                url={'https://youtu.be/e16JnsA9Q1s'}
                // controls
                width='100%'
                height='100%'
                style={{
                  maxWidth: '90vw',
                  aspectRatio: '16/9',
                  borderRadius: '10px',
                  overflow: 'hidden',
                }}
              />
              <Typography variant='h5' fontWeight={700}>
                Welcome!{' '}
                <SparklesIcon
                  sx={{
                    fontSize: 24,
                    color: '#FFD700',
                  }}
                />
              </Typography>
              <Typography variant='body2' textAlign='center'>
                In our live sessions, we gather for an hour each day to improve our speaking skills!
              </Typography>
              <Button
                fullWidth
                variant='contained'
                onClick={() => setShowExpectations(true)}
                sx={{
                  py: 1,
                  fontWeight: 500,
                  backgroundColor: 'rgba(114, 255, 187, 0.5)',
                  color: 'black',
                }}
              >
                What to Expect
              </Button>
              {event.lesson_link !== '' && (
                <Button
                  component={Link}
                  href={event.lesson_link}
                  target='_blank'
                  rel='noopener'
                  fullWidth
                  variant='contained'
                  sx={{
                    py: 1,
                    fontWeight: 500,
                    backgroundColor: 'rgba(97, 187, 254, 0.5)',
                    color: 'black',
                  }}
                >
                  Today's Discussion Questions
                </Button>
              )}
            </Stack>
            {isHost ? (
              <Stack spacing={1} sx={{ alignItems: 'center' }}>
                <Typography variant='h5' color='primary'>
                  {t('loadingScreen.hosting', 'You are hosting this event')}
                </Typography>
                <Typography variant='body1'>
                  {t('loadingScreen.scheduledTime', 'Please return at the sheduled time')}
                </Typography>
                {/* TODO EDIT EVENT */}
                {/* TODO MODAL TO CANCEL EVENT */}
                <CancelEvent eventId={event.event_id} />
                <Button
                  variant='contained'
                  onClick={handleStartEvent}
                  loading={isLoadingStartEvent}
                >
                  {t('loadingScreen.startNow', 'Start event now')}
                </Button>
              </Stack>
            ) : event.participants.find((participant) => participant.id === user?.id) ? (
              <Stack spacing={1} sx={{ alignItems: 'center' }}>
                <Typography variant='h5' color='primary'>
                  {t('loadingScreen.going', 'You are going to this event')}
                </Typography>
                <Typography variant='body1'>
                  {t('loadingScreen.return', 'Please return at the sheduled time')}
                </Typography>
                <Button
                  variant='outlined'
                  onClick={handleCancelParticipation}
                  loading={isLoadingCancelParticipant}
                  color='neutral'
                  sx={{ color: 'initial' }}
                >
                  {t('loadingScreen.cancel', 'Cancel participation')}
                </Button>
              </Stack>
            ) : (
              <Button
                loading={isLoadingParticipateInEvent}
                variant='contained'
                onClick={handleParticipate}
                sx={{ textTransform: 'initial' }}
              >
                {t('loadingScreen.participate', 'I want to participate')}
              </Button>
            )}
          </>
        )}
      </Layout>
    )
  }
  return null
}

const Layout = (props: {
  startTime: string
  children: React.ReactNode
  illustrationChildren: React.ReactNode
  headerTitle: React.ReactNode
  lobbyTitle: string
  eventId: string
  users: PublicUserProfile[]
  minNumberUsers?: number
  maxNumberUsers?: number
}) => {
  const { children, illustrationChildren, headerTitle } = props
  const matches = useMediaQuery(900)
  // const toast = useToast()
  const { event } = useCurrentEvent()
  // const { t } = useTranslation()
  // const handleCopySuccess = () => {
  //   toast(t('game-loading-screen.alert.success', " The game's code has been copied "), 'success')
  // }
  return (
    <Grid container height='100vh'>
      {!matches && (
        <Grid
          size={{ xs: 12, md: 5 }}
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Illustration />
          {illustrationChildren}
        </Grid>
      )}
      <Grid
        size={{ xs: 12, md: 7 }}
        sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        <Container maxWidth='md' sx={{ flex: 1 }}>
          <Stack sx={{ height: '100%' }}>
            <Stack
              direction='row'
              alignItems={{ xs: 'flex-start', md: 'center' }}
              justifyContent='space-between'
              sx={{ borderBottom: '1.04978px solid #0F82FF', mt: 1 }}
            >
              <Stack alignItems='center' spacing={1} sx={{ mb: 1 }}>
                <Typography
                  fontWeight='bold'
                  color='#738598'
                  // align='center'
                  sx={{ fontSize: { xs: 12, md: 14 } }}
                >
                  Scheduled for{' '}
                  {dayjs.utc(event?.start_time_utc).local().format('DD/MM/YYYY HH:mm')}
                </Typography>
                <Countdown
                  size={'xs'}
                  startTime={dayjs.utc(event?.start_time_utc, 'YYYY-MM-DDTHH:mm:ss').toDate()}
                  sx={{ mx: 'auto' }}
                />
                {event && matches && (
                  <Button
                    size={'small'}
                    variant='contained'
                    startIcon={
                      <img
                        src={googleCalendarIcon}
                        alt='google calendar'
                        style={{ width: '16px', height: '16px' }}
                      />
                    }
                    onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                      e.preventDefault()
                      window.open(createGoogleCalendarUrl(event), '_blank')
                    }}
                    sx={{
                      mx: 'auto',
                      width: { xs: '130px', sm: 'auto' },
                      backgroundColor: '#0061FD',
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: '10px',
                    }}
                  >
                    Add to Calendar
                  </Button>
                )}
              </Stack>
              <ModeTypography variant='body1' noWrap>
                {headerTitle}
              </ModeTypography>
            </Stack>
            {/* <ShareLink
                label='Copy'
                link={`${window.location.origin}/classroom/${eventId}`}
                onCopy={handleCopySuccess}
                sx={{ maxWidth: 400 }}
              /> */}
            {/* </HeaderContainer> */}
            <Stack
              sx={{
                flex: 1,
                py: 1,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {children}
            </Stack>
            {/* <PlayersLobby
              title={lobbyTitle}
              users={users}
              minNumberUsers={minNumberUsers}
              maxNumberUsers={maxNumberUsers}
            /> */}
          </Stack>
        </Container>
      </Grid>
    </Grid>
  )
}

const UpcomingEventDetails = (props: {
  title: string | null | undefined
  host: PublicUserProfile
  language: string
  start_time: Date
}) => {
  const { title, host, language, start_time } = props
  const { event } = useCurrentEvent()
  const diffDays = dayjs(start_time).diff(dayjs(), 'day', true)
  const { getLanguageFromCode } = useLanguages()
  const smMatch = useMediaQuery(600)
  return (
    <Stack
      sx={{
        alignItems: 'center',
        background: '#EAEAEA',
        color: 'black',
        borderRadius: '20px',
        p: 3,
      }}
    >
      <Typography
        sx={{
          width: '100%',
          textDecoration: 'underline',
          textUnderlineOffset: '10px',
          mb: '17px',
        }}
      >
        {getLanguageFromCode(language)?.name} room
      </Typography>
      {smMatch ? (
        <></>
      ) : (
        <Stack
          direction='row'
          sx={{ justifyContent: 'center', alignItems: 'center', mb: 1 }}
          spacing={1}
        >
          <Avatar
            src={handleGravatar(host?.image || '')}
            sx={{ width: 56, height: 56 }}
            variant='circular'
          />
          <Typography>{host?.display_name || host?.username}</Typography>
        </Stack>
      )}
      {title && (
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '32.2746px',
            lineHeight: '48px',
          }}
        >
          “{title}”
        </Typography>
      )}
      {diffDays < 1 && diffDays > 0 && (
        <Box mb={1}>
          <Typography color='#738598' textAlign='center' sx={{ mb: 0.5 }}>
            Starting In
          </Typography>
          <Countdown
            size={smMatch ? 'small' : 'medium'}
            startTime={start_time}
            sx={{ mx: 'auto' }}
          />
        </Box>
      )}
      {event && (
        <Button
          variant='contained'
          startIcon={
            <img
              src={googleCalendarIcon}
              alt='google calendar'
              style={{ width: '16px', height: '16px' }}
            />
          }
          onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            e.preventDefault()
            window.open(createGoogleCalendarUrl(event), '_blank')
          }}
          sx={{
            width: { xs: '80px', sm: 'auto' },
            backgroundColor: '#0061FD',
            color: '#fff',
            fontWeight: 600,
            fontSize: { xs: '10px', sm: '14px' },
          }}
        >
          Add to Calendar
        </Button>
      )}
      <Typography fontWeight='bold' color='#738598' align='center'>
        Scheduled for {dayjs.utc(event?.start_time_utc).local().format('DD/MM/YYYY HH:mm')}
      </Typography>
    </Stack>
  )
}

export default LoadingScreen
